import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { consultarInmuebles, getPropertyTypes,getMunicipiosYBarrios } from '../services/api';
import { FaFilter, FaSort, FaTh, FaList } from 'react-icons/fa';
import PropertyCard from '../components/PropertyCard';

const PropertiesPage = () => {
  const location = useLocation();
  
  // Estados principales
  const [inmuebles, setInmuebles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' o 'grid'
  
  // Estados para filtros
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [filtros, setFiltros] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      cantidadporpagina: 12,
      pagina: 1,
      destinacion: searchParams.get('destinacion') || '',
      municipio: searchParams.get('municipio') || '',
      barrio: searchParams.get('barrio') || '',
      clase: searchParams.get('clase') || '',
      alcobas: 0,
      banos: 0,
      preciodesde: 0,
      preciohasta: 0,
      areadesde: 0,
      areahasta: 0,
      ascensor: false,
      piscina: false,
      unidadcerrada: false,
      parqueadero: false,
      destacado: false,
      inmueble_lujo: false,
      order: 'fecha_consignacion',
      type_order: 'desc'
    };
  });
  
  // Estados para datos auxiliares
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInmuebles, setTotalInmuebles] = useState(0);
  const [locations,setLocations] = useState([])

  // SEO
  useSEO({
    title: 'Propiedades - J&D Propiedad Raíz',
    description: 'Encuentra la propiedad perfecta en Medellín y el Valle de Aburrá. Apartamentos, casas y locales en venta y arriendo con la mejor asesoría inmobiliaria.',
    keywords: 'propiedades, inmuebles, apartamentos, casas, venta, arriendo, Medellín, Valle de Aburrá',
    ogTitle: 'Propiedades en Venta y Arriendo - J&D Propiedad Raíz',
    ogDescription: 'Descubre las mejores propiedades en Medellín. Apartamentos, casas y locales con precios competitivos y ubicaciones privilegiadas.',
    ogImage: 'https://jdpropiedadraiz.co/logo192.png',
  });

  // Obtener parámetros de búsqueda de la URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setFiltros(prev => ({
      ...prev,
      destinacion: searchParams.get('destinacion') || '',
      clase: searchParams.get('clase') || '',
      municipio: searchParams.get('municipio') || '',
      barrio: searchParams.get('barrio') || '',
      preciodesde: searchParams.get('preciodesde') ? Number(searchParams.get('preciodesde')) : null,
      preciohasta: searchParams.get('preciohasta') ? Number(searchParams.get('preciohasta')) : null,
      pagina: 1
    }));
  }, [location.search]);

  // Cargar datos auxiliares
  useEffect(() => {
    const loadAuxiliaryData = async () => {
      try {
        // Ejecutamos ambas consultas al mismo tiempo para que sea más rápido
        const [tipos, ubicaciones] = await Promise.all([
          getPropertyTypes(),
          getMunicipiosYBarrios()
        ]);
        setPropertyTypes(tipos);
        setLocations(ubicaciones); // Necesitarás crear este estado (useState)
      } catch (error) {
        console.error('Error cargando datos auxiliares:', error);
      }
    };
    
    loadAuxiliaryData();
  }, []);

  const buscarInmuebles = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const resultados = await consultarInmuebles(filtros);
      setInmuebles(resultados);
      
      if (resultados.length > 0) {
        setTotalPages(resultados[0].totalpaginas || 1);
        setTotalInmuebles(resultados[0].totalinmuebles || 0);
      }
    } catch (error) {
      setError('Error al cargar las propiedades. Por favor, intenta de nuevo.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  // Consultar inmuebles cuando cambien los filtros
  useEffect(() => {
    buscarInmuebles();
  }, [buscarInmuebles]);

  const handleFilterChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor,
      pagina: 1 // Resetear a la primera página
    }));
  };

  const handlePageChange = (nuevaPagina) => {
    console.log("hh",nuevaPagina);
    setFiltros(prev => ({
      ...prev,
      pagina: nuevaPagina
    }));
  };


  const sortOptions = [
    { value: 'fecha_consignacion-desc', label: 'Más recientes', order: 'fecha_consignacion', type_order: 'desc' },
    { value: 'fecha_ofrecer_inmueble-desc', label: 'Fecha de oferta reciente', order: 'fecha_ofrecer_inmueble', type_order: 'desc' },
    { value: 'precio-asc', label: 'Menor precio', order: 'precio', type_order: 'asc' },
    { value: 'precio-desc', label: 'Mayor precio', order: 'precio', type_order: 'desc' },
    { value: 'consecutivo-desc', label: 'Más nuevos por código', order: 'consecutivo', type_order: 'desc' },
    { value: 'consecutivo-asc', label: 'Más antiguos por código', order: 'consecutivo', type_order: 'asc' },
  ];

  // 1. Extraer una lista limpia de municipios únicos (sin duplicados)
  const municipiosUnicos = useMemo(() => {
    if (!locations || locations.length === 0) return [];
    
    // Filtramos duplicados basándonos en el nombre del municipio
    const unicos = Array.from(
      new Map(locations.map(loc => [loc.municipio, loc.municipio])).values()
    );
    return unicos.sort(); // Los ordenamos alfabéticamente
  }, [locations]);

  // 2. Extraer los barrios SOLO del municipio seleccionado
  const barriosDisponibles = useMemo(() => {
    if (!filtros.municipio || !locations || locations.length === 0) return [];
    
    // Filtramos las locaciones que pertenecen al municipio actual
    const locacionesDelMunicipio = locations.filter(
      loc => loc.municipio === filtros.municipio
    );
    // Extraemos los barrios sin duplicados
    const todosLosBarrios = locacionesDelMunicipio.flatMap(loc => loc.barrios);

    const barriosUnicos = Array.from(
      new Map(todosLosBarrios.map(loc => [loc.barrio, loc.barrio])).values()
    );

    return barriosUnicos.sort();
  }, [locations, filtros.municipio]);

  // 3. Función especial para cambiar el municipio
  // (Si el usuario cambia de ciudad, debemos borrar el barrio que tenía seleccionado)
  const handleMunicipioChange = (nuevoMunicipio) => {
    setFiltros(prev => ({
      ...prev,
      municipio: nuevoMunicipio,
      barrio: '', // Reseteamos el barrio porque cambió la ciudad
      pagina: 1
    }));
  };

  // 4. Lógica para los rangos de precio dinámicos
  const priceRanges = useMemo(() => {
    if (filtros.destinacion === 'Venta') {
      return [
        { label: "0 - $200'000.000", desde: 0, hasta: 200000000 },
        { label: "$200'000.001 - $300'000.000", desde: 200000001, hasta: 300000000 },
        { label: "$300'000.001 - $400'000.000", desde: 300000001, hasta: 400000000 },
        { label: "Más de $400'000.000", desde: 400000001, hasta: 99999999999 }
      ];
    }
    // Por defecto (Arriendo) o si no hay destinación
    return [
      { label: "0 - $1'500.000", desde: 0, hasta: 1500000 },
      { label: "$1'500.001 - $3'000.000", desde: 1500001, hasta: 3000000 },
      { label: "Más de $3'000.000", desde: 3000001, hasta: 99999999999 }
    ];
  }, [filtros.destinacion]);

  // Función para manejar el cambio de precio múltiple
  const handlePriceChange = (value) => {
    if (!value) {
      setFiltros(prev => ({ ...prev, preciodesde: null, preciohasta: null, pagina: 1 }));
      return;
    }
    // Separamos el string "desde-hasta" en dos variables
    const [desde, hasta] = value.split('-');
    setFiltros(prev => ({
      ...prev,
      preciodesde: Number(desde),
      preciohasta: Number(hasta),
      pagina: 1
    }));
  };

  return (
    <div className="min-h-screen bg-brand-black text-brand-white">
      {/* Header de la página */}
      <div className="pt-24 pb-8 px-6 border-b border-brand-gold/20">
        <div className="container mx-auto max-w-7xl">
          <h1 className="text-4xl font-bold text-brand-gold mb-4">
            Propiedades
          </h1>
          {totalInmuebles > 0 && (
            <p className="text-brand-gray">
              {totalInmuebles} propiedades encontradas
            </p>
          )}
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Sidebar de filtros - Móvil colapsado */}
          <div className={`lg:w-1/4 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-brand-gold/5 p-6 rounded-lg border border-brand-gold/20 sticky top-24">
              <h2 className="text-xl font-semibold text-brand-gold mb-6">
                Filtros de búsqueda
              </h2>
              
              {/* Filtros aquí - implementar según necesidades */}
              <div className="space-y-4">
                {/* Destinación */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Tipo de servicio
                  </label>
                  <select
                    value={filtros.destinacion}
                    onChange={(e) => {
                      setFiltros(prev => ({
                        ...prev,
                        destinacion: e.target.value,
                        preciodesde: null, // Reseteamos el precio al cambiar de servicio
                        preciohasta: null,
                        pagina: 1
                      }));
                    }}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value="">Todos</option>
                    <option value="Arriendo">Arriendo</option>
                    <option value="Venta">Venta</option>
                  </select>
                </div>

                {/* Tipo de propiedad */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Tipo de propiedad
                  </label>
                  <select
                    value={filtros.clase}
                    onChange={(e) => handleFilterChange('clase', e.target.value)}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value="">Todos</option>
                    {propertyTypes.map(tipo => (
                      <option key={tipo} value={tipo}>
                        {tipo}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Filtro de Municipio */}
                 <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Municipio
                  </label>
                  <select
                    value={filtros.municipio}
                    onChange={(e) => handleMunicipioChange(e.target.value)}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value="">Todos los municipios</option>
                    {municipiosUnicos.map(municipio => (
                      <option key={municipio} value={municipio}>
                        {municipio}
                      </option>
                    ))}
                  </select>
                </div> 

                {/* Filtro de Barrio (Solo se habilita si hay un municipio seleccionado) */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Barrio
                  </label>
                  <select
                    value={filtros.barrio}
                    onChange={(e) => handleFilterChange('barrio', e.target.value)}
                    disabled={!filtros.municipio} // Se bloquea si no hay municipio
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {!filtros.municipio ? 'Selecciona un municipio primero' : 'Todos los barrios'}
                    </option>
                    {barriosDisponibles.map(barrio => (
                      <option key={barrio} value={barrio}>
                        {barrio}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Rango de Precio */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Rango de Precio
                  </label>
                  <select
                    // Verificamos si hay precio para marcar la opción seleccionada, sino va vacío
                    value={filtros.preciodesde !== null && filtros.preciohasta !== null ? `${filtros.preciodesde}-${filtros.preciohasta}` : ""}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    disabled={!filtros.destinacion} // Se bloquea si no hay Venta o Arriendo seleccionado
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {!filtros.destinacion ? 'Selecciona un servicio primero' : 'Cualquier precio'}
                    </option>
                    {priceRanges.map((range, index) => (
                      <option key={index} value={`${range.desde}-${range.hasta}`}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>



                {/* Alcobas */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Habitaciones
                  </label>
                  <select
                    value={filtros.alcobas}
                    onChange={(e) => handleFilterChange('alcobas', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value={0}>Cualquiera</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                  </select>
                </div>

                {/* Baños */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Baños
                  </label>
                  <select
                    value={filtros.banos}
                    onChange={(e) => handleFilterChange('banos', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value={0}>Cualquiera</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                  </select>
                </div>

                {/* Características especiales */}
                <div>
                  <label className="block text-brand-gold font-medium mb-3">
                    Características
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.ascensor}
                        onChange={(e) => handleFilterChange('ascensor', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Ascensor</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.piscina}
                        onChange={(e) => handleFilterChange('piscina', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Piscina</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.parqueadero}
                        onChange={(e) => handleFilterChange('parqueadero', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Parqueadero</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.unidadcerrada}
                        onChange={(e) => handleFilterChange('unidadcerrada', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Unidad cerrada</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contenido principal */}
          <div className="lg:w-3/4">
            {/* Barra de herramientas */}
            <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
              {/* Botones móviles */}
              <div className="flex gap-2 lg:hidden">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-4 py-2 bg-brand-gold/20 text-brand-gold rounded-lg border border-brand-gold/40"
                >
                  <FaFilter className="mr-2" size={14} />
                  Filtros
                </button>
              </div>

              {/* Vista y ordenamiento */}
              <div className="flex items-center gap-4">
                {/* Ordenamiento */}
                <div className="relative">
                  <button
                    onClick={() => setShowSort(!showSort)}
                    className="flex items-center px-4 py-2 bg-brand-gold/20 text-brand-gold rounded-lg border border-brand-gold/40"
                  >
                    <FaSort className="mr-2" size={14} />
                    Ordenar
                  </button>
                  
                  {showSort && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-brand-black border border-brand-gold/20 rounded-lg shadow-lg z-10">
                      {sortOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => {
                            handleFilterChange('order', option.order);
                            handleFilterChange('type_order', option.type_order);
                            setShowSort(false);
                          }}
                          className="w-full text-left px-4 py-2 text-brand-white hover:bg-brand-gold/20 first:rounded-t-lg last:rounded-b-lg"
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Cambio de vista */}
                <div className="flex border border-brand-gold/40 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-brand-gold text-brand-black' : 'bg-transparent text-brand-gold'}`}
                  >
                    <FaList size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-brand-gold text-brand-black' : 'bg-transparent text-brand-gold'}`}
                  >
                    <FaTh size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Resultados */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto mb-4"></div>
                <p className="text-brand-gray">Cargando propiedades...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-400 mb-4">{error}</p>
                <button
                  onClick={buscarInmuebles}
                  className="px-6 py-2 bg-brand-gold text-brand-black rounded-lg hover:bg-brand-gold/90"
                >
                  Reintentar
                </button>
              </div>
            ) : inmuebles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-brand-gray mb-4">No se encontraron propiedades con los filtros seleccionados.</p>
                <button
                  onClick={() => setFiltros({ ...filtros, pagina: 1 })}
                  className="px-6 py-2 bg-brand-gold text-brand-black rounded-lg hover:bg-brand-gold/90"
                >
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <>
                {/* Lista de propiedades */}
                <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 'space-y-6'}`}>
                  {inmuebles.map(inmueble => (
                    <PropertyCard
                      key={inmueble.consecutivo}
                      inmueble={inmueble}
                      isGrid={viewMode === 'grid'}
                    />
                  ))}
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-12">
                    <div className="flex gap-2">
                      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-4 py-2 rounded-lg ${
                              filtros.pagina === page
                                ? 'bg-brand-gold text-brand-black'
                                : 'bg-brand-gold/20 text-brand-gold hover:bg-brand-gold/30'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertiesPage;
